import { useState } from 'react';
import { PosPrintData, PosPrintOptions } from '@plick/electron-pos-printer';
import { capturePrintError } from '@utils/print/printSentry';
import { OrderDetail } from '@interfaces/OrderTypes';
import { useDetailedOrders } from '@pages/Orders/hooks/orders';
import toast from 'react-hot-toast';
import { buildElectronOrderTicket, buildElectronKitchenTicket } from '@pages/Orders/utils/electronTicket/buildTicket';
import { buildPrintOptions } from '@utils/buildElectronPrintOptions';
import { ElectronPrintSettings } from '@interfaces/general';
import { TICKET_TYPE_MAP } from '@utils/constants';
import { appStore } from '@store/index';

type Electron = {
  ipcRenderer: {
    invoke: <T>(channel: string, data?: unknown) => Promise<T>;
  };
};

type WindowWithElectron = {
  electron?: Electron;
} & Window;

type PrintOrderType = {
  detailedOrder?: OrderDetail;
  orderId: string;
  settings: ElectronPrintSettings;
};

type PrintSelfServiceOrderType = {
  orderIDs: string[];
  settings: ElectronPrintSettings;
};

export const useElectron = () => {
  const electron: Electron = (window as WindowWithElectron)?.electron || {
    ipcRenderer: {
      invoke: () => {
        throw new Error('Electron is not available, use the Electron app to use this features instead');
      },
    },
  };
  const isElectronInstance = Boolean((window as WindowWithElectron)?.electron?.ipcRenderer);
  const { ipcRenderer } = electron;
  const [isPrinting, setIsPrinting] = useState(false);
  const [isBuildingOrderTicket, setIsBuildingOrderTicket] = useState(false);
  const { getOrder } = useDetailedOrders();

  /**
   * Use this function to send data to the electron app to print it
   * @param {PosPrintData[]} data
   * @param {PosPrintOptions} options PLICK NOTE: Use the buildPrintOptions function inside a try catch to build the options, it will throw an error if the printer name is not set.
   */
  const print = async (data: PosPrintData[], options: PosPrintOptions) => {
    try {
      setIsPrinting(true);
      await ipcRenderer.invoke('print', { data, options });
    } catch (error) {
      capturePrintError(error, { tag: 'print_ipc_failure' });
    } finally {
      setIsPrinting(false);
    }
  };

  const printOrder = async ({ detailedOrder, orderId, settings }: PrintOrderType) => {
    if (!isElectronInstance) {
      return;
    }

    const {
      electronPrinterName,
      electronPrintTextSize,
      electronPrintLogo,
      electronPrintClientNumber,
      electronPrintCommerceData,
      electronRightMargin,
      electronLeftMargin,
      electronTicketType,
    } = settings;

    try {
      setIsBuildingOrderTicket(true);
      const commerce = appStore.getState().commerce;
      const options = buildPrintOptions({
        printerName: electronPrinterName,
        marginRightInPX: electronRightMargin,
        marginLeftInPX: electronLeftMargin,
      });
      const order = detailedOrder || (await getOrder(orderId));

      const isSimplifiedTicket = electronTicketType === TICKET_TYPE_MAP.simplified.key;
      const { ticket } = isSimplifiedTicket
        ? buildElectronKitchenTicket({
            order,
            commerceLogo: commerce?.logotipo && /^https?:\/\//.test(commerce?.logotipo || '') ? commerce.logotipo : '',
            commerceName: commerce.nombre,
            textSize: electronPrintTextSize,
            showLogo: electronPrintLogo && Boolean(commerce?.logotipo) && /^https?:\/\//.test(commerce?.logotipo || ''),
          })
        : buildElectronOrderTicket({
            order,
            referencesLabel: commerce?.config_form_entrega?.referencias?.label,
            commerceAddress: commerce?.address?.fullAddress || commerce.direccion || '',
            commerceLogo: commerce?.logotipo && /^https?:\/\//.test(commerce?.logotipo || '') ? commerce.logotipo : '',
            commerceName: commerce.nombre,
            textSize: electronPrintTextSize,
            showClientPhone: electronPrintClientNumber,
            showCommerceAddress: electronPrintCommerceData,
            showLogo: electronPrintLogo && Boolean(commerce?.logotipo) && /^https?:\/\//.test(commerce?.logotipo || ''),
          });

      print(ticket, options);
    } catch (error) {
      if (error?.toString().includes('Printer name is required')) {
        capturePrintError(error, { tag: 'printer_not_selected', extra: { orderId } });
        toast.error('No se pudo imprimir, selecciona una impresora en los ajustes');
        return;
      }

      toast.error('Error al obtener los detalles del pedido o el pedido no existe');
    } finally {
      setIsBuildingOrderTicket(false);
    }
  };

  const printSelfServiceOrder = async ({ orderIDs, settings }: PrintSelfServiceOrderType) => {
    if (!isElectronInstance) {
      return;
    }

    const {
      electronPrinterName,
      electronPrintTextSize,
      electronPrintLogo,
      electronPrintClientNumber,
      electronPrintCommerceData,
      electronRightMargin,
      electronLeftMargin,
      electronTicketType,
    } = settings;

    try {
      setIsBuildingOrderTicket(true);
      const commerce = appStore.getState().commerce;
      const options = buildPrintOptions({
        printerName: electronPrinterName,
        marginRightInPX: electronRightMargin,
        marginLeftInPX: electronLeftMargin,
      });

      const response = await Promise.all(orderIDs.map((id) => getOrder(id)));

      if (!response || response.length === 0) {
        toast.error('No se encontraron pedidos para imprimir');
        return;
      }

      const selfServiceDetailedOrders = response.sort((a, b) => {
        const aDate = new Date(a?.pedido?.created_at || 0);
        const bDate = new Date(b?.pedido?.created_at || 0);
        return aDate.getTime() - bDate.getTime();
      });

      const orderData = {
        pedido: {
          ...selfServiceDetailedOrders[0].pedido,
          folio: `mesa: ${selfServiceDetailedOrders[0].pedido?.self_service?.table_number}`,
          subtotal: selfServiceDetailedOrders.reduce((acc, order) => acc + Number(order.pedido.subtotal), 0),
          total: selfServiceDetailedOrders.reduce((acc, order) => acc + Number(order.pedido.total), 0),
          coupon_amount: selfServiceDetailedOrders.reduce((acc, order) => acc + Number(order.pedido.coupon_amount), 0),
          comentario: '',
        },
        productos: selfServiceDetailedOrders.map((order) => order.productos).flat(),
      };

      const isSimplifiedTicket = electronTicketType === TICKET_TYPE_MAP.simplified.key;
      const { ticket } = isSimplifiedTicket
        ? buildElectronKitchenTicket({
            order: orderData,
            commerceLogo: commerce?.logotipo && /^https?:\/\//.test(commerce?.logotipo || '') ? commerce.logotipo : '',
            commerceName: commerce.nombre,
            textSize: electronPrintTextSize,
            showLogo: electronPrintLogo && Boolean(commerce?.logotipo) && /^https?:\/\//.test(commerce?.logotipo || ''),
          })
        : buildElectronOrderTicket({
            order: orderData,
            referencesLabel: commerce?.config_form_entrega?.referencias?.label,
            commerceAddress: commerce?.address?.fullAddress || commerce.direccion || '',
            commerceLogo: commerce?.logotipo && /^https?:\/\//.test(commerce?.logotipo || '') ? commerce.logotipo : '',
            commerceName: commerce.nombre,
            textSize: electronPrintTextSize,
            showClientPhone: electronPrintClientNumber,
            showCommerceAddress: electronPrintCommerceData,
            showLogo: electronPrintLogo && Boolean(commerce?.logotipo) && /^https?:\/\//.test(commerce?.logotipo || ''),
          });

      print(ticket, options);
    } catch (error) {
      if (error?.toString().includes('Printer name is required')) {
        capturePrintError(error, { tag: 'printer_not_selected', trigger: 'self_service', extra: { orderIDs } });
        toast.error('No se pudo imprimir, selecciona una impresora en los ajustes');
        return;
      }

      toast.error('Error al obtener los detalles del pedido o el pedido no existe');
    } finally {
      setIsBuildingOrderTicket(false);
    }
  };

  // Reimpresión multi-impresora: dispara printOrder por cada impresora recibida,
  // respetando electronPrintNumber (copias) y el delay escalonado, igual que el
  // auto-print (useIncomingOrder). El caller decide las impresoras vía useReprintPrinters().
  const reprintOrder = ({
    orderId,
    detailedOrder,
    printers,
  }: {
    orderId: string;
    detailedOrder?: OrderDetail;
    printers: ElectronPrintSettings[];
  }) => {
    printers.forEach((settings) => {
      const copies = Math.max(1, settings.electronPrintNumber || 1);
      new Array(copies).fill(0).forEach((_, index) => {
        setTimeout(
          () => printOrder({ orderId, detailedOrder, settings }),
          (settings.electronMultiPrintDelayBetweenPrints || 0) * index * 1000,
        );
      });
    });
  };

  // Variante self-service (agrega varios orderIDs en un solo ticket por impresora).
  const reprintSelfServiceOrder = ({
    orderIDs,
    printers,
  }: {
    orderIDs: string[];
    printers: ElectronPrintSettings[];
  }) => {
    printers.forEach((settings) => {
      const copies = Math.max(1, settings.electronPrintNumber || 1);
      new Array(copies).fill(0).forEach((_, index) => {
        setTimeout(
          () => printSelfServiceOrder({ orderIDs, settings }),
          (settings.electronMultiPrintDelayBetweenPrints || 0) * index * 1000,
        );
      });
    });
  };

  return {
    isElectronInstance,
    ipcRenderer,
    printOrder,
    print,
    isAutoPrinting: isBuildingOrderTicket || isPrinting,
    printSelfServiceOrder,
    reprintOrder,
    reprintSelfServiceOrder,
  };
};
